import { useState, useEffect } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { Arrow } from './Arrow';

export function CTA() {
  const [time, setTime] = useState("");
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: "",
    surname: "",
    email: "",
    company: "",
    role: "",
    type: [],
    budget: "",
    timeline: "",
    message: "",
  });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [formKey, setFormKey] = useState(0);

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setTime(now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Yerevan" }));
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setOne = (k, v) => setForm({ ...form, [k]: v });
  const toggleMany = (k, v) => {
    const has = form[k].includes(v);
    setForm({ ...form, [k]: has ? form[k].filter(x => x !== v) : [...form[k], v] });
  };

  const types = ["Product design", "Design system", "UX research", "Brand & identity", "Mobile app", "Web app / SaaS", "AI-native UX", "Creative direction"];
  const budgets = ["< €8k", "€8k – €25k", "€25k – €60k", "€60k – €120k", "€120k +"];
  const timelines = ["ASAP / urgent", "Within 1 month", "1 – 3 months", "3 – 6 months", "Flexible / TBD"];

  const steps = [
    {
      label: "First, your name",
      hint: "Let's keep it personal.",
      valid: () => form.name.trim().length > 0 && form.surname.trim().length > 0,
      render: () => (
        <div className="cta-step-row">
          <label className="cta-step-field">
            <span className="cta-step-sublabel">Name</span>
            <input required type="text" placeholder="Maria" value={form.name} onChange={set("name")} />
          </label>
          <label className="cta-step-field">
            <span className="cta-step-sublabel">Surname</span>
            <input required type="text" placeholder="Sokolova" value={form.surname} onChange={set("surname")} />
          </label>
        </div>
      ),
    },
    {
      label: "How can I reach you?",
      hint: "I reply within one working day.",
      valid: () => /\S+@\S+\.\S+/.test(form.email),
      render: () => (
        <label className="cta-step-field">
          <input required type="email" placeholder="you@company.com" value={form.email} onChange={set("email")} />
        </label>
      ),
    },
    {
      label: "Tell me about you and the project",
      hint: "Company, role, what we'll be working on.",
      valid: () => form.company.trim().length > 0,
      render: () => (
        <div className="cta-step-row">
          <label className="cta-step-field">
            <span className="cta-step-sublabel">Company or project</span>
            <input required type="text" placeholder="Acme Inc. / Halt" value={form.company} onChange={set("company")} />
          </label>
          <label className="cta-step-field">
            <span className="cta-step-sublabel">Your role</span>
            <input type="text" placeholder="Founder, PM, Head of Design…" value={form.role} onChange={set("role")} />
          </label>
        </div>
      ),
    },
    {
      label: "What are you looking for?",
      hint: "Pick anything that applies.",
      valid: () => form.type.length > 0,
      render: () => (
        <div className="cta-pro-chips">
          {types.map((t) => (
            <button key={t} type="button" className={`cta-pro-chip ${form.type.includes(t) ? "active" : ""}`} onClick={() => toggleMany("type", t)}>{t}</button>
          ))}
        </div>
      ),
    },
    {
      label: "Budget range",
      hint: "Helps me scope the right approach.",
      valid: () => form.budget !== "",
      render: () => (
        <div className="cta-pro-chips">
          {budgets.map((b) => (
            <button key={b} type="button" className={`cta-pro-chip ${form.budget === b ? "active" : ""}`} onClick={() => setOne("budget", b)}>{b}</button>
          ))}
        </div>
      ),
    },
    {
      label: "When would you like to start?",
      hint: "Approximate is fine.",
      valid: () => form.timeline !== "",
      render: () => (
        <div className="cta-pro-chips">
          {timelines.map((t) => (
            <button key={t} type="button" className={`cta-pro-chip ${form.timeline === t ? "active" : ""}`} onClick={() => setOne("timeline", t)}>{t}</button>
          ))}
        </div>
      ),
    },
    {
      label: "Tell me about the project",
      hint: "What are you building? What does success look like?",
      valid: () => form.message.trim().length > 10,
      render: () => (
        <label className="cta-step-field">
          <textarea required rows={5} placeholder="The more detail, the better." value={form.message} onChange={set("message")} />
        </label>
      ),
    },
    {
      label: "One last thing",
      hint: "Verify you're human to send the brief.",
      valid: () => captchaToken !== "",
      render: () => (
        <div className="cta-step-field" style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
          <HCaptcha
            sitekey="50b2fe65-b00b-4b9e-ad62-3ba471098be2"
            reCaptchaCompat={false}
            onVerify={(token) => setCaptchaToken(token)}
            onExpire={() => setCaptchaToken("")}
          />
        </div>
      ),
    },
  ];

  const total = steps.length;
  const cur = steps[step];
  const isLast = step === total - 1;
  
  const onNext = async () => {
    if (!cur.valid()) return;
    
    if (isLast) {
      setSubmitting(true);
      setSubmitError(false);
      
      try {
        // Prepare form data for Web3Forms
        const formData = new FormData();
        formData.append("access_key", "d9ea8aca-a45e-4380-ba2b-afb446096994");
        formData.append("name", `${form.name} ${form.surname}`);
        formData.append("email", form.email);
        formData.append("company", form.company);
        formData.append("role", form.role);
        formData.append("type", form.type.join(", "));
        formData.append("budget", form.budget);
        formData.append("timeline", form.timeline);
        formData.append("message", form.message);
        formData.append("h-captcha-response", captchaToken);

        const response = await fetch("https://api.web3forms.com/submit", {
          method: "POST",
          body: formData
        });

        const data = await response.json();
        
        if (data.success) {
          setSent(true);
        } else {
          setSubmitError(true);
        }
      } catch (error) {
        console.error("Form submission error:", error);
        setSubmitError(true);
      } finally {
        setSubmitting(false);
      }
      
      return;
    }
    
    setStep(step + 1);
  };
  
  const onBack = () => { 
    if (step > 0) setStep(step - 1); 
  };
  
  const onKeyDown = (e) => {
    if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
      e.preventDefault();
      onNext();
    }
  };

  if (sent) {
    return (
      <section className="cta-pro dark" id="contact">
        <div className="container cta-pro-sent">
          <div className="cta-pro-checkmark">
            <svg viewBox="0 0 24 24" width="56" height="56" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <h2 className="display cta-pro-sent-title">Message received.</h2>
          <p className="cta-pro-sent-lede">
            Thanks {form.name || "for reaching out"}. I'll get back to you within one working day. If it's urgent, write directly to <a href="mailto:dee.productdesign@gmail.com">dee.productdesign@gmail.com</a>.
          </p>
          <button type="button" className="btn btn-ghost" onClick={() => { 
            setSent(false); 
            setStep(0); 
            setSubmitError(false);
            setCaptchaToken("");
            setFormKey(prev => prev + 1);
            setForm({ name: "", surname: "", email: "", company: "", role: "", type: [], budget: "", timeline: "", message: "" }); 
          }}>Send another <Arrow /></button>
        </div>
      </section>
    );
  }

  const progress = ((step + (cur.valid() ? 1 : 0)) / total) * 100;

  return (
    <section className="cta-pro dark" id="contact">
      <div className="cta-pro-bg">
        <div className="cta-pro-glow" data-parallax="0.08" />
        <div className="cta-pro-grid" />
      </div>

      <div className="container cta-pro-inner">
        <header className="cta-pro-header" key={formKey}>
          <div className="cta-pro-tag">
            <span className="pulse" />
            <span>Available · Yerevan {time && <span className="cta-pro-tag-time">{time}</span>}</span>
          </div>
          <h2 className="display cta-pro-title" data-reveal>
            Let's start<br />
            <em>a project.</em>
          </h2>
          <p className="cta-pro-lede" data-reveal style={{ "--reveal-delay": "120ms" }}>
            A few quick questions. I read every reply myself.
          </p>
        </header>

        <form className="cta-step-form" onKeyDown={onKeyDown} onSubmit={(e) => { e.preventDefault(); onNext(); }}>
          <div className="cta-step-progress">
            <div className="cta-step-progress-meta">
              <span className="cta-step-num">Step {step + 1} of {total}</span>
              <span className="cta-step-pct">{Math.round(progress)}%</span>
            </div>
            <div className="cta-step-bar">
              <div className="cta-step-bar-fill" style={{ width: progress + "%" }} />
            </div>
          </div>

          <div className="cta-step-body" key={step}>
            <h3 className="cta-step-label">{cur.label}</h3>
            <p className="cta-step-hint">{cur.hint}</p>
            <div className="cta-step-input">{cur.render()}</div>
          </div>

          <footer className="cta-step-footer">
            <button type="button" className="cta-step-back" onClick={onBack} disabled={step === 0}>← Back</button>
            <button type="button" className="cta-step-next" onClick={onNext} disabled={!cur.valid() || submitting}>
              {submitting ? "Sending..." : (isLast ? "Send the brief" : "Continue")} <Arrow />
            </button>
            {submitError && <p style={{ color: "#ff6b6b", marginTop: "10px", fontSize: "14px" }}>Error sending message. Please try again or email directly.</p>}
          </footer>
        </form>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="footer dark">
      <div className="container">
        <div className="footer-top">
          <div className="footer-mark display">Dee.</div>
          <div className="footer-cols">
            <div>
              <span className="eyebrow">Studio</span>
              <a href="#work">Work</a>
              <a href="#services">Services</a>
              <a href="#process">Process</a>
              <a href="#contact">Contact</a>
            </div>
            <div>
              <span className="eyebrow">Channels</span>
              <a href="https://www.behance.net/DianaHakobyan1" target="_blank" rel="noopener noreferrer">Behance</a>
              <a href="https://www.instagram.com/dee_uxuidesign/?hl=en" target="_blank" rel="noopener noreferrer">Instagram</a>
              <a href="https://medium.com/@dianahakobyan111" target="_blank" rel="noopener noreferrer">Medium</a>
              <a href="https://www.linkedin.com/in/diana-hakobyan-64ab04b5" target="_blank" rel="noopener noreferrer">LinkedIn</a>
            </div>
            <div>
              <span className="eyebrow">Talk</span>
              <a href="mailto:dee.productdesign@gmail.com">dee.productdesign@gmail.com</a>
              <a href="https://t.me/DianaUIUX" target="_blank" rel="noopener noreferrer">Telegram</a>
            </div>
          </div>
        </div>

        <div className="footer-bot">
          <span>© 2014 - 2026 Diana Hakobyan. All rights reserved</span>
          <span><span className="pulse-dot" />Studio open</span>
        </div>
      </div>
    </footer>
  );
}
